import { useEffect, useMemo, useState } from "react";
import { HiArrowLeft, HiBell, HiCog, HiDeviceMobile, HiPencilAlt, HiSearch, HiUserGroup, HiVolumeUp, HiX } from "react-icons/hi";
import ChatAvatar from "./ChatAvatar";
import ConversationListItem from "./ConversationListItem";
import CreateGroupModal from "./CreateGroupModal";
import { useChat } from "./ChatProvider";
import { deletePushSubscription, fetchPushPublicKey, savePushSubscription, searchChatMessages } from "../../controllers/chatController";
import { disablePush, enablePush, getPushSubscription, pushSupported } from "../../services/pwa";
import { formatLastSeen, formatListTime, otherMemberId } from "./chatUtils";

const SectionLabel = ({ children }) => (
  <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{children}</p>
);

function Highlighted({ text, query }) {
  const words = query.trim().split(/\s+/).filter(Boolean).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!words.length || !text) return text || null;
  const parts = text.split(new RegExp(`(${words.join("|")})`, "gi"));
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="bg-yellow-200 dark:bg-yellow-700 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function PersonRow({ user, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-left"
    >
      <ChatAvatar name={user.name} src={user.avatarThumbUrl} seed={user.id} size="sm" online={user.online} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium truncate text-gray-800 dark:text-gray-100">{user.name}</span>
        <span className="block text-xs text-gray-500 truncate">{user.jobTitle ? `${user.jobTitle} · ` : ""}{formatLastSeen(user)}</span>
      </span>
    </button>
  );
}

function SettingsRow({ icon: Icon, label, hint, checked, onChange, disabled }) {
  return (
    <label className={`flex items-start gap-3 px-3 py-2 rounded-lg ${disabled ? "opacity-60" : "hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"}`}>
      <Icon className="w-5 h-5 mt-0.5 text-gray-500" />
      <span className="flex-1">
        <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">{label}</span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
      <input type="checkbox" className="mt-1 rounded text-blue-600" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

const PUSH_ERRORS = {
  "no-service-worker": "Available in the installed app or on the live site.",
  "permission-denied": "Notifications are blocked in your browser settings for this site.",
  "not-configured": "Push notifications aren't set up on the server yet.",
};

// "Notify me when the app is closed": Web Push for this browser/device.
function usePushSetting() {
  const [state, setState] = useState({ enabled: false, busy: false, error: "" });
  useEffect(() => {
    getPushSubscription()
      .then((sub) => setState((s) => ({ ...s, enabled: Boolean(sub) })))
      .catch(() => {});
  }, []);
  const toggle = async (on) => {
    setState((s) => ({ ...s, busy: true, error: "" }));
    try {
      if (on) {
        const { enabled, publicKey } = await fetchPushPublicKey();
        if (!enabled) throw new Error("not-configured");
        await savePushSubscription(await enablePush(publicKey));
      } else {
        const endpoint = await disablePush();
        if (endpoint) await deletePushSubscription(endpoint);
      }
      setState({ enabled: on, busy: false, error: "" });
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: PUSH_ERRORS[err.message] || "Couldn't change push notifications." }));
    }
  };
  return { ...state, toggle };
}

function SettingsMenu() {
  const { settings, updateSettings } = useChat();
  const push = usePushSetting();
  const desktopBlocked = "Notification" in window && Notification.permission === "denied";
  return (
    <div className="px-1 pb-2">
      <SectionLabel>Notifications</SectionLabel>
      <SettingsRow icon={HiVolumeUp} label="Message sound" hint="Beep when a new message arrives" checked={settings.sound} onChange={(sound) => updateSettings({ sound })} />
      <SettingsRow
        icon={HiBell}
        label="Desktop notifications"
        hint={desktopBlocked ? "Blocked in your browser settings for this site" : "Show a system notification when the app is in the background"}
        checked={settings.desktop}
        disabled={desktopBlocked}
        onChange={(desktop) => updateSettings({ desktop })}
      />
      {pushSupported() && (
        <SettingsRow
          icon={HiDeviceMobile}
          label="Notify me when the app is closed"
          hint={push.error || "Direct messages and mentions arrive as notifications on this device even when IRG Chat isn't open"}
          checked={push.enabled}
          disabled={push.busy}
          onChange={push.toggle}
        />
      )}
    </div>
  );
}

// Conversation list with search, new chat / new group and settings. Used by the header panel and the chat page.
function ConversationBrowser({ activeId, onSelect, headerExtra }) {
  const { me, users, sortedConversations, status, titleOf, startDirectChat, focusMessage, setConversationPinned } = useChat();
  const [notice, setNotice] = useState("");
  const [mode, setMode] = useState("list"); // list | people | settings
  const [query, setQuery] = useState("");
  const [messageResults, setMessageResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);

  const q = query.trim().toLowerCase();

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const togglePin = (conversation) =>
    setConversationPinned(conversation.id, !conversation.pinnedAt).catch((err) =>
      setNotice(err?.response?.data?.message || "Couldn't update the pin. Try again.")
    );

  useEffect(() => {
    if (mode !== "list" || q.length < 2) {
      setMessageResults([]);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        setMessageResults(await searchChatMessages(q));
      } catch {
        setMessageResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, mode]);

  const matchingConversations = useMemo(
    () => (q ? sortedConversations.filter((c) => titleOf(c).toLowerCase().includes(q)) : sortedConversations),
    [q, sortedConversations, titleOf]
  );

  const people = useMemo(() => {
    const list = Object.values(users).filter((u) => u.id !== me?.id);
    return list
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      .sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name));
  }, [users, me, q]);

  // While searching the list, also offer people you haven't chatted with yet.
  const directPartnerIds = useMemo(
    () => new Set(sortedConversations.filter((c) => c.type === "direct").map((c) => otherMemberId(c, me?.id))),
    [sortedConversations, me]
  );
  const newPeople = q ? people.filter((u) => !directPartnerIds.has(u.id)) : [];

  const startChat = async (userId) => {
    const conversation = await startDirectChat(userId);
    setMode("list");
    setQuery("");
    onSelect(conversation.id);
  };

  const conversationsById = Object.fromEntries(sortedConversations.map((c) => [c.id, c]));

  return (
    <div className="chat-ui flex flex-col h-full min-h-0 text-gray-800 dark:text-gray-100">
      <div className="px-3 pt-3 pb-2 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-1 mb-2">
          {mode !== "list" ? (
            <button type="button" onClick={() => setMode("list")} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Back">
              <HiArrowLeft className="w-5 h-5" />
            </button>
          ) : null}
          <h2 className="flex-1 text-base font-semibold">
            {mode === "people" ? "New chat" : mode === "settings" ? "Chat settings" : "Chat"}
          </h2>
          {status !== "connected" && (
            <span className="text-[11px] text-amber-600 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
              {status === "connecting" ? "Connecting…" : "Reconnecting…"}
            </span>
          )}
          {mode === "list" && (
            <>
              <button type="button" onClick={() => setMode("people")} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" title="New chat" aria-label="New chat" data-tour="chat-new">
                <HiPencilAlt className="w-5 h-5" />
              </button>
              <button type="button" onClick={() => setShowGroupModal(true)} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" title="New group" aria-label="New group" data-tour="chat-new-group">
                <HiUserGroup className="w-5 h-5" />
              </button>
              <button type="button" onClick={() => setMode("settings")} className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" title="Settings" aria-label="Chat settings" data-tour="chat-settings">
                <HiCog className="w-5 h-5" />
              </button>
            </>
          )}
          {headerExtra}
        </div>
        {mode !== "settings" && (
          <div className="relative">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={mode === "people" ? "Search people" : "Search people, chats and messages"}
              data-tour="chat-search"
              className="w-full pl-9 pr-8 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600" aria-label="Clear search">
                <HiX className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-1 py-1" data-tour="chat-list">
        {mode === "settings" && <SettingsMenu />}

        {mode === "people" &&
          (people.length ? people.map((u) => <PersonRow key={u.id} user={u} onClick={() => startChat(u.id)} />) : (
            <p className="px-3 py-6 text-sm text-center text-gray-500">No people found.</p>
          ))}

        {mode === "list" && (
          <>
            {notice && (
              <p role="status" className="mx-2 my-1 rounded-md bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                {notice}
              </p>
            )}
            {q && matchingConversations.length > 0 && <SectionLabel>Chats</SectionLabel>}
            {matchingConversations.map((c, i) => (
              <div key={c.id}>
                {/* Without a search, pinned chats get their own section at the top. */}
                {!q && i === 0 && c.pinnedAt && <SectionLabel>Pinned</SectionLabel>}
                {!q && i > 0 && !c.pinnedAt && matchingConversations[i - 1].pinnedAt && <SectionLabel>All chats</SectionLabel>}
                <ConversationListItem conversation={c} active={c.id === activeId} onSelect={onSelect} onTogglePin={togglePin} />
              </div>
            ))}

            {newPeople.length > 0 && <SectionLabel>People</SectionLabel>}
            {newPeople.map((u) => (
              <PersonRow key={u.id} user={u} onClick={() => startChat(u.id)} />
            ))}

            {q.length >= 2 && (
              <>
                <SectionLabel>Messages {searching && "…"}</SectionLabel>
                {!searching && messageResults.length === 0 && <p className="px-3 py-2 text-xs text-gray-500">No messages found.</p>}
                {messageResults.map((m) => {
                  const conversation = conversationsById[m.conversationId];
                  const sender = users[m.senderId];
                  return (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => focusMessage(m.conversationId, m.id)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <span className="flex items-center justify-between gap-2 text-xs text-gray-500">
                        <span className="truncate">
                          {sender?.id === me?.id ? "You" : sender?.name || "Unknown"}
                          {conversation ? ` · ${titleOf(conversation)}` : ""}
                        </span>
                        <span className="shrink-0">{formatListTime(m.createdAt)}</span>
                      </span>
                      <span className="block text-sm line-clamp-2 break-words">
                        <Highlighted text={m.body || m.attachments.map((a) => a.fileName).join(", ")} query={q} />
                      </span>
                    </button>
                  );
                })}
              </>
            )}

            {!q && sortedConversations.length === 0 && (
              <div className="px-6 py-10 text-center">
                <p className="text-sm text-gray-500 mb-3">No conversations yet.</p>
                <button type="button" onClick={() => setMode("people")} className="px-4 py-2 rounded-lg bg-[#2470B7] hover:bg-[#1d5a93] text-white text-sm font-medium">
                  Start a chat
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showGroupModal && (
        <CreateGroupModal onClose={() => setShowGroupModal(false)} onCreated={(c) => onSelect(c.id)} />
      )}
    </div>
  );
}

export default ConversationBrowser;
